import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Leaf, ArrowLeft, Building2, CheckCircle, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTIVITY_TYPES = [
  { id: "isolation", label: "Isolation thermique" },
  { id: "chauffage", label: "Chauffage / Climatisation" },
  { id: "menuiserie", label: "Menuiserie / Fenêtres" },
  { id: "plomberie", label: "Plomberie" },
  { id: "electricite", label: "Électricité" },
  { id: "generale", label: "Entreprise générale" },
];

const CERTIFICATIONS = [
  { id: "rge", label: "RGE (Reconnu Garant de l'Environnement)" },
  { id: "qualibat", label: "Qualibat" },
  { id: "qualifelec", label: "Qualifelec" },
  { id: "qualipac", label: "QualiPAC" },
  { id: "qualibois", label: "Qualibois" },
  { id: "qualigaz", label: "Qualigaz" },
];

export default function ProfessionnelForm() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    company_name: "",
    siret: "",
    contact_first_name: "",
    contact_last_name: "",
    email: "",
    phone: "",
    address: "",
    postal_code: "",
    city: "",
    activity_type: [],
    certifications: [],
    partnership_reason: "",
    additional_info: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleArrayToggle = (field, value) => {
    const currentArray = formData[field];
    if (currentArray.includes(value)) {
      setFormData({ ...formData, [field]: currentArray.filter(v => v !== value) });
    } else {
      setFormData({ ...formData, [field]: [...currentArray, value] });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.company_name || !formData.siret || !formData.email || !formData.phone) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    if (formData.siret.length !== 14) {
      toast.error("Le numéro SIRET doit contenir 14 chiffres");
      return;
    }

    if (formData.activity_type.length === 0) {
      toast.error("Veuillez sélectionner au moins un domaine d'activité");
      return;
    }

    if (!formData.partnership_reason) {
      toast.error("Veuillez préciser votre motivation pour le partenariat");
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/partners`, formData);
      setSubmitted(true);
      toast.success("Votre demande de partenariat a été envoyée !");
    } catch (error) {
      const message = error.response?.data?.detail || "Erreur lors de l'envoi";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-4">
        <div className="max-w-lg text-center animate-fade-in">
          <div className="w-20 h-20 bg-[#E8F5E9] rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-10 w-10 text-[#1A4D2E]" />
          </div>
          <h1 className="text-3xl font-bold text-[#1D1D1D] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Demande envoyée !
          </h1>
          <p className="text-slate-600 mb-8">
            Votre demande de partenariat a bien été enregistrée. Notre équipe l'examinera dans les plus brefs délais et vous contactera par email.
          </p>
          <Link to="/">
            <Button className="bg-[#1A4D2E] hover:bg-[#143d24] text-white h-12 px-8" data-testid="back-home-btn">
              Retour à l'accueil
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-home">
            <Leaf className="h-7 w-7 text-[#1A4D2E]" />
            <span className="text-xl font-bold text-[#1A4D2E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              MesSubventions
            </span>
          </Link>
          <Link 
            to="/"
            className="text-slate-500 hover:text-[#1A4D2E] text-sm flex items-center gap-1 transition-colors"
            data-testid="exit-form"
          >
            <ArrowLeft className="h-4 w-4" />
            Quitter
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 py-12">
        {/* Header Section */}
        <div className="text-center mb-12 animate-fade-in">
          <div className="w-16 h-16 bg-[#E07A5F]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Building2 className="h-8 w-8 text-[#E07A5F]" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#1D1D1D] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Devenir partenaire MesSubventions
          </h1>
          <p className="text-slate-600 max-w-xl mx-auto">
            Rejoignez notre réseau d'artisans qualifiés et accédez à des leads qualifiés dans votre région.
          </p>
        </div>

        {/* Benefits */}
        <div className="grid md:grid-cols-3 gap-4 mb-12">
          {[
            { icon: "📍", title: "Leads locaux", desc: "Clients dans votre zone" },
            { icon: "✅", title: "Qualifiés", desc: "Projets validés et prêts" },
            { icon: "🚀", title: "Croissance", desc: "Développez votre activité" },
          ].map((benefit, i) => (
            <div key={i} className="bg-white rounded-xl p-5 border border-slate-100 text-center">
              <span className="text-2xl">{benefit.icon}</span>
              <h3 className="font-semibold text-[#1D1D1D] mt-2">{benefit.title}</h3>
              <p className="text-sm text-slate-500">{benefit.desc}</p>
            </div>
          ))}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8" data-testid="partner-form">
          <h2 className="text-xl font-bold text-[#1D1D1D] mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Informations de l'entreprise
          </h2>

          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="company_name">Raison sociale *</Label>
                <Input
                  id="company_name"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  placeholder="Entreprise Dupont SARL"
                  className="h-12"
                  data-testid="input-company-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="siret">SIRET (14 chiffres) *</Label>
                <Input
                  id="siret"
                  name="siret"
                  value={formData.siret}
                  onChange={handleChange}
                  placeholder="12345678901234"
                  maxLength={14}
                  className="h-12"
                  data-testid="input-siret"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contact_first_name">Prénom du contact *</Label>
                <Input
                  id="contact_first_name"
                  name="contact_first_name"
                  value={formData.contact_first_name}
                  onChange={handleChange}
                  placeholder="Jean"
                  className="h-12"
                  data-testid="input-contact-firstname"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact_last_name">Nom du contact *</Label>
                <Input
                  id="contact_last_name"
                  name="contact_last_name"
                  value={formData.contact_last_name}
                  onChange={handleChange}
                  placeholder="Dupont"
                  className="h-12"
                  data-testid="input-contact-lastname"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email professionnel *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="contact@entreprise.fr"
                  className="h-12"
                  data-testid="input-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Téléphone *</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="01 23 45 67 89"
                  className="h-12"
                  data-testid="input-phone"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Adresse de l'entreprise *</Label>
              <Input
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="12 rue de l'Industrie"
                className="h-12"
                data-testid="input-address"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="postal_code">Code postal *</Label>
                <Input
                  id="postal_code"
                  name="postal_code"
                  value={formData.postal_code}
                  onChange={handleChange}
                  placeholder="75001"
                  className="h-12"
                  data-testid="input-postal"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="city">Ville *</Label>
                <Input
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="Paris"
                  className="h-12"
                  data-testid="input-city"
                />
              </div>
            </div>

            {/* Activity Types */}
            <div className="space-y-4">
              <Label>Domaines d'activité *</Label>
              <div className="grid md:grid-cols-2 gap-3">
                {ACTIVITY_TYPES.map((activity) => (
                  <div
                    key={activity.id}
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                      formData.activity_type.includes(activity.id)
                        ? 'border-[#1A4D2E] bg-[#E8F5E9]'
                        : 'border-slate-200 hover:border-[#81B29A]'
                    }`}
                    onClick={() => handleArrayToggle("activity_type", activity.id)}
                    data-testid={`activity-${activity.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={formData.activity_type.includes(activity.id)}
                        onCheckedChange={() => handleArrayToggle("activity_type", activity.id)}
                        className="data-[state=checked]:bg-[#1A4D2E] data-[state=checked]:border-[#1A4D2E]"
                      />
                      <span className="font-medium text-[#1D1D1D]">{activity.label}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Certifications */}
            <div className="space-y-4">
              <Label className="flex items-center gap-2">
                <BadgeCheck className="h-5 w-5 text-[#1A4D2E]" />
                Certifications
              </Label>
              <div className="grid md:grid-cols-2 gap-3">
                {CERTIFICATIONS.map((cert) => (
                  <div
                    key={cert.id}
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                      formData.certifications.includes(cert.id)
                        ? 'border-[#E07A5F] bg-[#FBE9E7]'
                        : 'border-slate-200 hover:border-[#E07A5F]/50'
                    }`}
                    onClick={() => handleArrayToggle("certifications", cert.id)}
                    data-testid={`cert-${cert.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={formData.certifications.includes(cert.id)}
                        onCheckedChange={() => handleArrayToggle("certifications", cert.id)}
                        className="data-[state=checked]:bg-[#E07A5F] data-[state=checked]:border-[#E07A5F]"
                      />
                      <span className="font-medium text-[#1D1D1D] text-sm">{cert.label}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Motivation */}
            <div className="space-y-2">
              <Label htmlFor="partnership_reason">Pourquoi souhaitez-vous devenir partenaire ? *</Label>
              <Textarea
                id="partnership_reason"
                name="partnership_reason"
                value={formData.partnership_reason}
                onChange={handleChange}
                placeholder="Décrivez votre motivation et comment vous pensez pouvoir contribuer..."
                className="min-h-[120px]"
                data-testid="input-partnership-reason"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="additional_info">Informations complémentaires</Label>
              <Textarea
                id="additional_info"
                name="additional_info"
                value={formData.additional_info}
                onChange={handleChange}
                placeholder="Zone d'intervention, équipe, références..."
                className="min-h-[80px]"
                data-testid="input-additional-info"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-14 bg-[#E07A5F] hover:bg-[#cc6b52] text-white text-lg font-medium btn-shine"
              data-testid="submit-partner-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></span>
                  Envoi en cours...
                </span>
              ) : (
                <>
                  Soumettre ma demande
                  <CheckCircle className="h-5 w-5 ml-2" />
                </>
              )}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
